// System Admin › Integrations (B10 plan D14, §6.1; B11 C-B11-11; CR-063 "Integration failures have retry and
// recovery."; G §9 "Failed sync is visible and retryable."). Drawn for a reader who holds B11's `admin.integrations`:
// SystemAdmin.tsx leaves the tab out and answers not-found otherwise, and register.ts gates `#admin/integrations/<is_…>`
// on the same tool. Per port: whether it runs on its mock or live, how many calls wait on someone, when it last
// succeeded; each open failure as D0's Sync failure strip — *Retry* (`admin.integration_retry`) and *Manual recovery*
// behind the matrix *Override* (`admin.integration_resolve`, with a reason: "Admin override only with reason and
// audit."). The row a link names is also read on its own (`admin.integration_open`, D14), so a recovery task's link
// shows that row and its history even once it is no longer open. Every call and type is B11's generated client.

import { useState } from "react";
import {
  Card, CORE_LABELS, Dialog, EmptyState, ERROR_LABELS, Field, G1_LABELS, History, HISTORY_LABELS, KeyValue, Pill, Stack, Sync, SYNC_LABELS, Table, Td,
  TextArea, Th, Tr, formatDateTime, type HistoryEvent,
} from "@/mc";
import {
  adminIntegrationOpen, adminIntegrationResolve, adminIntegrationRetry, adminIntegrations,
  type AdminIntegrationOpenOutput, type AdminIntegrationsOutput, type OpenCall, type PortHealth,
} from "@/api/tools";
import { errorText, useRead, useWrite } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { SYSTEM_LABELS } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

export type { AdminIntegrationOpenOutput, AdminIntegrationsOutput, OpenCall, PortHealth };

/** The mail port's calls are its outbox rows (B11 `read.health`): those waiting on someone are failed or dead. */
const MAIL = "mail";
/** The statuses a call waits on someone in (B11 `read.OPEN_STATUSES`). */
const OPEN_STATUSES = ["failed", "unknown"] as const;

const epoch = (at: string) => {
  const n = Date.parse(at);
  return Number.isNaN(n) ? 0 : n;
};

/** One outbox attempt of a write (mail), as `admin.integration_open` lists it: when, the row's state and the attempt's
 * outcome, and whether it carried an error — never the provider's message. */
interface OutboxAttempt {
  at: string;
  state: string;
  outcome: string;
  hadError: boolean;
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");

function outboxAttempts(open: AdminIntegrationOpenOutput): OutboxAttempt[] {
  return (open.outbox_history ?? []).map((e) => ({
    at: text(e["at"]),
    state: text(e["state"]),
    outcome: text(e["outcome"]),
    hadError: e["had_error"] === true,
  }));
}

/** The linked row's history, newest first: its receipts (the call, its retries, a manual recovery, marked when a
 * reason was recorded) and its outbox attempts, each a D0 History row. The receipt carries no before/after of the row
 * and the reason stays in the ledger, so neither is drawn here; the full audit form is the Audit engine's. */
export function linkedEvents(open: AdminIntegrationOpenOutput): HistoryEvent[] {
  const receipts = open.receipts.map((r): HistoryEvent => ({
    id: `receipt-${r.id}`,
    sentence: (
      <>
        <span className="mc-mono">{r.tool}</span>
        {r.has_reason ? <> · {HISTORY_LABELS.reason}</> : null}
      </>
    ),
    at: r.at,
    tone: "info",
  }));
  const outbox = outboxAttempts(open).map((o, i): HistoryEvent => ({
    id: `outbox-${i}`,
    sentence: (
      <>
        <span className="mc-mono">{o.state}</span>
        {o.outcome ? <> · <span className="mc-mono">{o.outcome}</span></> : null}
      </>
    ),
    at: o.at,
    tone: o.hadError ? "red" : "info",
  }));
  return [...receipts, ...outbox].sort((a, b) => epoch(String(b.at)) - epoch(String(a.at)) || a.id.localeCompare(b.id));
}

/** The row a link names, read on its own: where it is, its state, how many attempts, and its history. */
export function LinkedSyncPanel({ open, loading }: { open?: AdminIntegrationOpenOutput | undefined; loading?: boolean | undefined }) {
  const row = open?.row;
  return (
    <Card
      title={SYSTEM_LABELS.integration}
      sub={row ? <span className="mc-mono">{row.id}</span> : undefined}
      loading={loading ? { lines: 4 } : undefined}
      
    >
      {open && row ? (
        <Stack>
          <KeyValue
            cols={3}
            items={[
              [SYSTEM_LABELS.integration, <span key="p" className="mc-mono">{row.port} · {row.op}</span>],
              [SYSTEM_LABELS.status, <span key="s" className="mc-mono">{row.status}</span>],
              [SYSTEM_LABELS.retries, <span key="r" className="mc-mono">{row.attempts}</span>],
            ]}
          />
          <History label={HISTORY_LABELS.audit} events={linkedEvents(open)} />
        </Stack>
      ) : null}
    </Card>
  );
}

/** The calls of a port that wait on someone: failed and unknown, or for mail its failed and dead outbox rows. */
export function openFailures(row: PortHealth): number {
  if (row.port === MAIL) return (row.outbox?.["failed"] ?? 0) + (row.outbox?.["dead"] ?? 0);
  return OPEN_STATUSES.reduce((sum, status) => sum + (row.counts[status] ?? 0), 0);
}

/** The failures in the order they are drawn: the one a link names first, then the oldest attempts first. */
export function orderedFailures(failures: readonly OpenCall[], focus?: string | undefined): OpenCall[] {
  return [...failures].sort((a, b) => Number(b.id === focus) - Number(a.id === focus) || b.attempts - a.attempts || a.id.localeCompare(b.id));
}

/** A reason the server will take: 3–500 characters once trimmed. */
export function reasonOk(reason: string): boolean {
  const n = reason.trim().length;
  return n >= 3 && n <= 500;
}

export interface IntegrationsPanelProps {
  health?: AdminIntegrationsOutput | undefined;
  loading?: boolean | undefined;
  focus?: string | undefined;
  canRetry: boolean;
  canRecover: boolean;
  retrying?: string | null | undefined;
  /** A retry the server refused, by row: its sentence replaces the row's one-line error. */
  refusals?: Readonly<Record<string, string>> | undefined;
  onRetry?: ((row: OpenCall) => void) | undefined;
  onRecover?: ((row: OpenCall) => void) | undefined;
}

/** The ports and their open failures: data in, markup out. */
export function IntegrationsPanel({ health, loading, focus, canRetry, canRecover, retrying, refusals, onRetry, onRecover }: IntegrationsPanelProps) {
  const failures = orderedFailures(health?.open ?? [], focus);
  return (
    <Stack>
      <Card title={SYSTEM_LABELS.integrations} flush >
        <Table
          dense
          label={SYSTEM_LABELS.integrations}
          loading={loading ? 3 : undefined}
          empty={!loading && (health?.ports ?? []).length === 0 ? <EmptyState inline>—</EmptyState> : undefined}
          head={(
            <Tr>
              <Th>{SYSTEM_LABELS.integration}</Th>
              <Th>{HISTORY_LABELS.source}</Th>
              <Th align="r">{CORE_LABELS.syncFailure}</Th>
              <Th>{ERROR_LABELS.lastAttempt}</Th>
            </Tr>
          )}
        >
          {(health?.ports ?? []).map((row) => (
            <Tr key={row.port}>
              <Td mono label={SYSTEM_LABELS.integration}>{row.port}</Td>
              <Td mono label={HISTORY_LABELS.source}>{row.adapter}</Td>
              <Td align="r" label={CORE_LABELS.syncFailure}>
                {openFailures(row) > 0 ? <Pill tone="red">{openFailures(row)}</Pill> : <span className="mc-mono">0</span>}
              </Td>
              <Td nowrap label={ERROR_LABELS.lastAttempt}>{row.last_success_at ? formatDateTime(row.last_success_at) : "—"}</Td>
            </Tr>
          ))}
        </Table>
      </Card>
      {failures.length > 0 ? (
        <Card title={CORE_LABELS.syncFailure} count={failures.length} >
          <Stack>
            {failures.map((row) => (
              <Sync
                key={row.id}
                status="failure"
                message={refusals?.[row.id] ?? <span className="mc-mono">{row.port} · {row.op}</span>}
                live={refusals?.[row.id] !== undefined}
                details={{
                  correlationId: row.correlation_id,
                  status: row.status,
                  retries: row.attempts,
                  error: [row.error_kind, row.http_status].filter((part) => part !== null && part !== undefined).join(" · ") || undefined,
                }}
                defaultOpen={row.id === focus}
                canRetry={canRetry}
                retrying={retrying === row.id}
                onRetry={() => onRetry?.(row)}
                canOverride={canRecover}
                onRecover={() => onRecover?.(row)}
                hs={hsKey("cfg.sys.integrations.retry", row.id)}
                recoverHs={hsKey("cfg.sys.integrations.recover", row.id)}
              />
            ))}
          </Stack>
        </Card>
      ) : null}
    </Stack>
  );
}

export interface RecoveryDialogProps {
  row: OpenCall | null;
  reason: string;
  onReason: (reason: string) => void;
  busy?: boolean | undefined;
  refused?: string | null | undefined;
  onConfirm: () => void;
  onClose: () => void;
}

/** *Manual recovery* after an out-of-band fix: his reason, stored on the receipt (DC-217 "every Override is a reason
 * dialog"). */
export function RecoveryDialog({ row, reason, onReason, busy, refused, onConfirm, onClose }: RecoveryDialogProps) {
  return (
    <Dialog
      open={row !== null}
      title={SYNC_LABELS.manualRecovery}
      onClose={onClose}
      primary={{
        label: SYNC_LABELS.manualRecovery,
        onClick: onConfirm,
        busy,
        blocked: reasonOk(reason) ? undefined : { reason: SYSTEM_LABELS.overrideNeedsReason },
        hs: "cfg.sys.integrations.recover.confirm",
      }}
      refused={refused ? { reason: refused } : undefined}
      hint={G1_LABELS.reasonStored}
    >
      {row ? (
        <Stack>
          <span className="mc-mono">{row.port} · {row.op} · {row.correlation_id}</span>
          <Field label={HISTORY_LABELS.reason} required hint={SYSTEM_LABELS.overrideNeedsReason}>
            <TextArea value={reason} maxLength={500} onChange={(event) => onReason(event.target.value)}  />
          </Field>
        </Stack>
      ) : null}
    </Dialog>
  );
}

const READ = ["cfg", "admin.integrations"] as const;
const LINKED = ["cfg", "admin.integration_open"] as const;

/** The section: reads B11's health, retries a failure, records a manual recovery. */
export function IntegrationsView({ focus }: { focus?: string | undefined }) {
  const { can } = useSession();
  const q = useRead(READ, () => adminIntegrations({}), { staleTime: 0 });
  // the row a recovery task links to, one audited read, only for a reader B11 lets open it
  const canOpen = focus !== undefined && can("admin.integration_open");
  const linked = useRead(
    [...LINKED, focus ?? ""],
    () => adminIntegrationOpen({ integration_sync_id: focus ?? "" }),
    { staleTime: 0, enabled: canOpen },
  );
  // B11 D11: a retry is idempotent per (call, attempts); a manual recovery takes a reason of 3–500 characters
  const retry = useWrite(
    (row: OpenCall) => adminIntegrationRetry({ integration_sync_id: row.id }, `retry-${row.id}-${row.attempts}`),
    [READ, LINKED],
  );
  const resolve = useWrite(
    (input: { row: OpenCall; reason: string }, key: string) =>
      adminIntegrationResolve({ integration_sync_id: input.row.id, reason: input.reason.trim() }, key),
    [READ, LINKED],
  );
  const [recovering, setRecovering] = useState<OpenCall | null>(null);
  const [reason, setReason] = useState("");
  const [refused, setRefused] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [refusals, setRefusals] = useState<Record<string, string>>({});

  if (q.error) {
    return <Card title={SYSTEM_LABELS.integrations} error={{ message: errorText(q.error), onRetry: () => void q.refetch(), details: { correlationId: q.error.requestId ?? undefined, status: q.error.status } }} />;
  }
  const close = () => { setRecovering(null); setReason(""); setRefused(null); };
  return (
    <>
      {canOpen && !linked.error ? <LinkedSyncPanel open={linked.data} loading={linked.isLoading} /> : null}
      {canOpen && linked.error ? (
        <Card
          title={SYSTEM_LABELS.integration}
          error={{ message: errorText(linked.error), onRetry: () => void linked.refetch(), details: { correlationId: linked.error.requestId ?? undefined, status: linked.error.status } }}
        />
      ) : null}
      <IntegrationsPanel
        health={q.data}
        loading={q.isLoading}
        focus={focus}
        canRetry={can("admin.integration_retry")}
        canRecover={can("admin.integration_resolve")}
        retrying={retryingId}
        refusals={refusals}
        onRetry={(row) => {
          setRetryingId(row.id);
          setRefusals((r) => { const next = { ...r }; delete next[row.id]; return next; });
          retry.mutateAsync(row).then(
            () => setRetryingId(null),
            (e: unknown) => { setRetryingId(null); setRefusals((r) => ({ ...r, [row.id]: errorText(e) })); },
          );
        }}
        onRecover={(row) => { setRecovering(row); setReason(""); setRefused(null); }}
      />
      <RecoveryDialog
        row={recovering}
        reason={reason}
        onReason={setReason}
        busy={resolve.isPending}
        refused={refused}
        onClose={close}
        onConfirm={() => {
          if (!recovering || !reasonOk(reason)) return;
          resolve.mutateAsync({ row: recovering, reason }).then(close, (e: unknown) => setRefused(errorText(e)));
        }}
      />
    </>
  );
}

